package com.iregained.site.components.sections.patients.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.patients.styles.ExperienceFeatureItemStyle
import com.iregained.site.components.sections.patients.styles.ExperienceImageStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Diagonal Rise
@Composable
private fun Modifier.staggeredDiagonalRise(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0)
        .translate(if (isVisible) 0.px else (-30).px, if (isVisible) 0.px else 30.px).transition(
            Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun PatientsMyHandFeaturesSection(data: PatientsMyHandFeaturesSectionData = defaultPatientsMyHandFeaturesSectionData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-myhand-experience"
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    if (data.features.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            val palette = ColorMode.current.toSitePalette()
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.headline.isNotBlank()) {
                        SectionHeader(
                            text = data.headline,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).staggeredDiagonalRise(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.description.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px)
                                .staggeredDiagonalRise(isVisible, 150).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.description; onDispose {}
                                    }
                                })
                    }

                    if (isDesktop) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(100.px),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column(modifier = Modifier.weight(1.3f).minWidth(0.px)) {
                                ExperienceContent(
                                    data, Modifier.padding(right = 40.px), isVisible = isVisible
                                )
                            }
                            Box(
                                modifier = Modifier.weight(1.0f).minWidth(0.px).opacity(if (isVisible) 1 else 0)
                                    .transition(Transition.of("opacity", 1.s, delay = 600.ms)),
                                contentAlignment = Alignment.CenterEnd
                            ) {
                                if (data.imagePath.isNotBlank()) Image(
                                    src = data.imagePath,
                                    alt = data.imageAlt,
                                    modifier = ExperienceImageStyle.toModifier().unextractable().fillMaxWidth()
                                        .maxHeight(500.px).maxWidth(1200.px).borderRadius(20.px)
                                        .objectFit(ObjectFit.Cover)
                                )
                            }
                        }
                    } else {
                        Column(
                            modifier = Modifier.fillMaxWidth().maxWidth(800.px),
                            verticalArrangement = Arrangement.spacedBy(50.px),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            ExperienceContent(data, Modifier.fillMaxWidth(), isMobile = true, isVisible = isVisible)
                            if (data.imagePath.isNotBlank()) Image(
                                src = data.imagePath,
                                alt = data.imageAlt,
                                modifier = ExperienceImageStyle.toModifier().unextractable().fillMaxWidth(95.percent)
                                    .height(350.px).borderRadius(12.px).objectFit(ObjectFit.Cover)
                                    .opacity(if (isVisible) 1 else 0)
                                    .transition(Transition.of("opacity", 1.s, delay = 600.ms))
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ExperienceContent(
    data: PatientsMyHandFeaturesSectionData, modifier: Modifier, isMobile: Boolean = false, isVisible: Boolean
) {
    val palette = ColorMode.current.toSitePalette()
    val align = if (isMobile) Alignment.CenterHorizontally else Alignment.Start

    Column(modifier = modifier, horizontalAlignment = align) {
        Column(modifier = Modifier.gap(30.px).fillMaxWidth()) {
            data.features.forEachIndexed { index, feature ->
                key(feature.title) {
                    if (feature.title.isNotBlank() || feature.description.isNotBlank()) {
                        Row(
                            modifier = ExperienceFeatureItemStyle.toModifier().fillMaxWidth()
                                .staggeredDiagonalRise(isVisible, 300 + (index * 150)),
                            verticalAlignment = Alignment.Top,
                            horizontalArrangement = if (isMobile) Arrangement.Center else Arrangement.Start
                        ) {
                            Image(
                                src = feature.iconPath,
                                alt = "${feature.title} icon",
                                modifier = Modifier.unextractable().size(75.px).margin(right = 20.px, top = 10.px)
                                    .borderRadius(50.percent).objectFit(ObjectFit.Cover)
                            )
                            Column(modifier = Modifier.weight(1f)) {
                                if (feature.title.isNotBlank()) SpanText(
                                    text = feature.title,
                                    modifier = Modifier.fontSize(20.px).fontWeight(FontWeight.Bold)
                                        .color(palette.custom.darkText).margin(bottom = 8.px)
                                )
                                if (feature.description.isNotBlank()) Div(
                                    attrs = Modifier.fontSize(16.px).color(palette.custom.darkText).lineHeight(1.6)
                                        .fontWeight(FontWeight.Medium).toAttrs {
                                            ref { htmlElement ->
                                                htmlElement.innerHTML = feature.description; onDispose {}
                                            }
                                        })
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================

data class GamifiedFeatures(
    val title: String, val description: String, val iconPath: String
)

/**
 * CMS IMAGE UPLOAD SPECS FOR THE MYHAND EXPERIENCE:
 * @field imagePath:
 * - Aspect Ratio: 3:4 (Vertical) or 1:1 (Square).
 * - Recommended Dimensions: 800x1000px.
 * - Format: WebP or high-quality JPEG.
 * - Content: Focus on the device screen showing the gamified app, perhaps with a patient's hand interacting with it.
 * * @field iconPath (inside ExperienceFeature):
 * - Aspect Ratio: 1:1.
 * - Recommended Dimensions: 48x48px.
 * - Format: SVG or transparent PNG. (Should be a monochrome icon that looks good in your primary Teal color).
 */
data class PatientsMyHandFeaturesSectionData(
    val sectionId: String? = null,
    val headline: String,
    val description: String,
    val features: List<GamifiedFeatures>,
    val imagePath: String,
    val imageAlt: String
)

val defaultPatientsMyHandFeaturesSectionData = PatientsMyHandFeaturesSectionData(
    sectionId = "patients-myhand-experience",
    headline = "ENGAGING THERAPEUTIC EXPERIENCE",
    description = "The MyHand<sup>®</sup> System makes high-intensity, repetitive task training—essential for neuroplasticity and recovery—both engaging and effective.",
    features = listOf(
        GamifiedFeatures(
            title = "Interactive Gaming Experience",
            description = "Experience a highly engaging video game interface that tracks every single finger movement for effective functional recovery while you have the fun.",
            iconPath = Res.Images.PatientsPage.MyHandExperience.ENGAGEMENT // Reusing existing engagement icon
        ), GamifiedFeatures(
            title = "Function Oriented Rehabilitation",
            description = "You decide your functional goal, the System then sets individual finger parameters for strength, precision and coordination training to help you achieve your goal.",
            iconPath = Res.Images.PatientsPage.MyHandExperience.NEUROPLASTICITY // Reusing existing therapy icon
        ), GamifiedFeatures(
            title = "Real-time Progress Monitoring",
            description = "Built-in advanced analytics to show all functional parameters and monitor progress in real-time, keeping you motivated and focussed on your recovery goals.",
            iconPath = Res.Images.PatientsPage.MyHandExperience.ANALYTICS // Reusing existing analytics icon
        )
    ),
    // NOTE: Add this image path to your AppConstants.kt -> Res.Images.PatientsPage
    imagePath = Res.Images.PatientsPage.MyHandExperience.DEVICE, // Placeholder using your existing app image
    imageAlt = "Patient playing interactive games on the MyHand System tablet"
)